/**
 * The submission lifecycle, in one place.
 *
 * Submission statuses were printed straight from the database with
 * `status.replace('_', ' ')` in six places. Three consequences, all visible:
 *
 *   1. Nothing translated. A French chair saw "submitted" and "revision
 *      required" in an otherwise French table — and after the column filter
 *      menus landed, the SAME status appeared translated in the filter and
 *      untranslated in the cell, two words apart on one screen.
 *   2. `replace('_', ' ')` without the /g flag only replaces the FIRST
 *      underscore, so a three-word status would have kept one.
 *   3. Six copies of the vocabulary is six chances to drift, exactly as
 *      happened with the conference statuses this file mirrors.
 *
 * Labels resolve from the `common` namespace, where the keys are camelCase
 * (`underReview`) while the database is snake_case (`under_review`) — that
 * mismatch is why the first version of the filter menu fell back to raw keys.
 * The conversion lives here now so no caller has to know about it.
 */

export const SUBMISSION_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'revision_required',
  'accepted',
  'rejected',
  'withdrawn',
];

/** English fallbacks, used when a translation is missing. */
const FALLBACK = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  revision_required: 'Revision required',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

/** `under_review` -> `underReview`, matching the keys in common.json. */
const toKey = (v) => String(v).replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/**
 * @param {string} value  raw database status
 * @param {Function} t    i18next `t`, from any namespace — the lookup is
 *                        explicitly scoped to `common`, so the caller's own
 *                        namespace doesn't matter.
 */
export function submissionStatusLabel(value, t) {
  if (!value) return '';
  const fallback = FALLBACK[value] || String(value).replace(/_/g, ' ');
  if (!t) return fallback;
  return t(`status.${toKey(value)}`, { ns: 'common', defaultValue: fallback });
}

export function submissionStatusOptions(t) {
  return SUBMISSION_STATUSES.map((value) => ({
    value,
    label: submissionStatusLabel(value, t),
  }));
}

export default SUBMISSION_STATUSES;
